import { useEffect, useRef } from 'react';
import { onEmailVerified } from '../gcp/emailVerifiedSignal';

/**
 * Let the verification screen move on without the "I've confirmed" click.
 *
 * Two triggers, and neither polls on a timer:
 *
 *   1. The other tab says it redeemed the link (BroadcastChannel). This is the
 *      common case: the link opens in a new tab of the same browser. It is a
 *      real signal, so it fires the check at once and is not throttled.
 *   2. This tab becomes visible or focused again. That covers a link opened on
 *      a phone or in another browser, where no message can reach us: the user
 *      comes back to this tab, and we ask the server then.
 *
 * WHY NOT A TIMER. The check is POST /auth/verify-email, which is rate limited
 * to 10 requests per 5 minutes per client. A poll every few seconds would burn
 * that budget in under a minute and then answer 429 to the manual button too,
 * so the fallback the user relies on would break. The server is unchanged by
 * design, so the client spends the budget carefully: focus-triggered checks
 * are spaced at least MIN_GAP_MS apart and capped at MAX_AUTO_CHECKS per
 * mount, which leaves half the window for the button.
 *
 * `check` must be SILENT on failure (the account simply is not verified yet),
 * and `isBusy` stops a focus event and a broadcast arriving together from
 * firing two requests.
 */
export const MIN_GAP_MS = 5_000;
export const MAX_AUTO_CHECKS = 5;

export function useAutoVerifyEmail({
  check,
  enabled,
  isBusy,
}: {
  check: () => void;
  enabled: boolean;
  isBusy: boolean;
}): void {
  const latest = useRef({ check, isBusy, enabled });
  latest.current = { check, isBusy, enabled };
  const lastAt = useRef(0);
  const count = useRef(0);

  useEffect(() => {
    const run = (throttled: boolean) => {
      const { check: doCheck, isBusy: busy, enabled: on } = latest.current;
      if (!on || busy) return;
      if (throttled) {
        const now = Date.now();
        if (count.current >= MAX_AUTO_CHECKS) return;
        if (now - lastAt.current < MIN_GAP_MS) return;
        count.current += 1;
        lastAt.current = now;
      }
      doCheck();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') run(true);
    };
    const onFocus = () => run(true);

    const unsubscribe = onEmailVerified(() => run(false));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
}
